import {
  ACTION_DESKTOP_NAV_MENU_DESTINATION,
  CATEGORY_NAV,
} from 'utils/analytics/eventTypesConstants';
import { Divider, List, ListItem, SiteMenu, SiteMenuTopLevelHeading, baseMenuId } from 'omakase';
import { ListItemElementEndProps } from 'omakase/ListItem/ListItemCore';
import Anchor from 'components/Anchor';
import React from 'react';

interface MenuElementCommonProps {
  /**
   * An optional boolean that, if falsy, will prevent a given menu item from rendering (e.g., intended to be used with feature flags, etc.)
   */
  conditional?: boolean;
}

interface MenuItemAnchor extends MenuElementCommonProps {
  listComponentType: 'itemAnchor';
  listComponentProps?: {
    elementStart?: React.ReactNode;
    elementEndProps?: ListItemElementEndProps;
  };
  name: string;
  href: string;
  urlToMatch: string;
  key: MenuItemKeys;
}

interface MenuItemButton extends MenuElementCommonProps {
  listComponentType: 'itemButton';
  listComponentProps?: {
    elementStart?: React.ReactNode;
    elementEndProps?: ListItemElementEndProps;
  };
  name: string;
  navigateTo?: string;
  key: MenuItemKeys;
  additionalOnClick?: () => void;
}

interface MenuDivider extends MenuElementCommonProps {
  listComponentType: 'divider';
  key: `divider${number}`;
}
type MenuElement = MenuItemAnchor | MenuDivider | MenuItemButton;

enum MenuItemKeys {
  YOUR_RESERVATIONS,
  SAVED_BUSINESSES,
  ACCOUNT,
  CONCIERGE_PORTAL,
  LOG_OUT,
  SUBMIT_FEEDBACK,
  SUBMIT_BUG,
  YOUR_WAITLISTS,
}
export interface DesktopSiteMenuImplProps {
  /**
   * Whether or not the menu should render partner items (e.g., Concierge portal)
   */
  isPartner: boolean;
  /**
   * Callback for closing the menu, should change the received `open` prop.
   */
  onClose(): void;
  /**
   * Current route pathname
   */
  pathname: string;
  /**
   * Patron name
   */
  patronName?: string;
  /**
   * Link to patron profile image
   */
  profileImageLink?: string;
}

/**
 * Implementation of SiteMenu for Consumer desktop
 */
export const DesktopSiteMenuImpl: React.FunctionComponent<DesktopSiteMenuImplProps> = ({
  isPartner,
  pathname,
  patronName,
  profileImageLink,
  onClose,
}) => {
  const menuData: MenuElement[] = React.useMemo(() => {
    return [
      {
        listComponentType: 'itemAnchor',

        name: 'Your reservations',
        href: '/profile/reservations',
        urlToMatch: 'profile/reservations',
        key: MenuItemKeys.YOUR_RESERVATIONS,
      },
      {
        listComponentType: 'itemAnchor',
        name: 'Notify',
        href: '/profile/reservations/waitlist',
        urlToMatch: 'profile/reservations/waitlist',
        key: MenuItemKeys.YOUR_WAITLISTS,
      },
      {
        listComponentType: 'itemAnchor',

        name: 'Saved businesses',
        href: '/profile/saved',
        urlToMatch: 'profile/saved',
        key: MenuItemKeys.SAVED_BUSINESSES,
      },
      {
        listComponentType: 'itemAnchor',

        name: 'Account',
        href: '/profile/account/profile',
        urlToMatch: 'profile/account',
        key: MenuItemKeys.ACCOUNT,
      },
      {
        listComponentType: 'itemAnchor',

        name: 'Concierge portal',
        href: '/concierge/teams',
        urlToMatch: '/concierge/teams',
        key: MenuItemKeys.CONCIERGE_PORTAL,
        conditional: isPartner,
      },
      {
        listComponentType: 'divider',
        key: 'divider2',
      },
      {
        listComponentType: 'itemAnchor',

        name: 'Log out',
        href: '/logout',
        urlToMatch: '/logout',
        key: MenuItemKeys.LOG_OUT,
      },
    ];
  }, [isPartner]);

  // Filter menu data for anchor items, to be used to determine which menu item is selected
  const menuDataFilteredForAnchor = menuData.filter(
    (menuElement) => menuElement?.listComponentType === 'itemAnchor'
  ) as MenuItemAnchor[];

  // Determine which menu item is selected, and which content page to render upon menu opening
  let foundMenuElement = menuDataFilteredForAnchor.find(
    ({ urlToMatch }) => pathname === urlToMatch
  );

  // If no exact match found, search 'includes' matches
  if (!foundMenuElement) {
    foundMenuElement = menuDataFilteredForAnchor.find(({ urlToMatch }) =>
      pathname.includes(urlToMatch)
    );
  }

  const selectedMenuItem = foundMenuElement?.key ?? undefined;

  // Send menu click analytics event
  const sendMenuClickAnalyticsEvent = (menuItemName: string) => {
    return window.analytics?.sendEvent(
      CATEGORY_NAV,
      ACTION_DESKTOP_NAV_MENU_DESTINATION,
      menuItemName
    );
  };

  /**
   * Build site menu, per <SiteMenu>'s builder function
   */
  const builder = React.useCallback(() => {
    /**
     * Generate list items for menu
     */
    const generateListFromMenuData = (menuData: MenuElement[]) => {
      return (
        <List>
          {menuData
            // Filter out any items that have a falsy conditional (items without a conditional will evaluate to true / not be filtered out)
            .filter(({ conditional }) => conditional !== false)
            .map((element) => {
              switch (element.listComponentType) {
                case 'itemAnchor':
                  return (
                    <ListItem
                      component={Anchor}
                      key={element.key}
                      href={element.href}
                      onClick={() => {
                        sendMenuClickAnalyticsEvent(element.name);
                        onClose();
                      }}
                      size="medium"
                      selected={selectedMenuItem === element.key}
                      variant="basic"
                      weight="book"
                      {...element.listComponentProps}
                      data-testid={`${element.name.replace(/\W/g, '').toLowerCase()}-link`}
                    >
                      {element.name}
                    </ListItem>
                  );
                case 'itemButton':
                  return element.name ? (
                    <ListItem
                      component={'button'}
                      key={element.key}
                      onClick={() => {
                        sendMenuClickAnalyticsEvent(element.name);
                        element.additionalOnClick?.();
                      }}
                      size="medium"
                      selected={selectedMenuItem === element.key}
                      variant="basic"
                      weight="book"
                      {...element.listComponentProps}
                    >
                      {element.name}
                    </ListItem>
                  ) : null;
                case 'divider':
                  return <Divider key={element.key} position="middle" component="li" />;
              }
            })}
        </List>
      );
    };

    return {
      [baseMenuId]: (
        <>
          <SiteMenuTopLevelHeading
            onClick={onClose}
            href={'/profile'}
            pathname={pathname}
            loggedIn={true}
            patronName={patronName}
            profileImageLink={profileImageLink}
          />
          {generateListFromMenuData(menuData)}
        </>
      ),
    };
  }, [menuData, selectedMenuItem, onClose, pathname, profileImageLink, patronName]);

  return (
    // The SiteMenu popover (and its visible state) are linked to state that lies in the DesktopHeader component (linked to a Hamburger Icon)
    <SiteMenu builder={builder} showInitialContentId={baseMenuId} />
  );
};
